import React, { useState } from 'react';
import { Navigate, Link } from 'react-router-dom';
import { 
  Calendar, 
  FileText, 
  Settings, 
  LogOut, 
  Users, 
  TrendingUp,
  Clock,
  CheckCircle,
  AlertCircle,
  Car
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { useAuth } from '../contexts/AuthContext';
import { useAPI } from '../hooks/useAPI';
import { bookingsAPI, contactsAPI } from '../services/api';

const AdminDashboard = () => {
  const { isAuthenticated, logout } = useAuth();

  // Récupérer les données
  const { data: bookings } = useAPI(() => bookingsAPI.getAll());
  const { data: contacts } = useAPI(() => contactsAPI.getAll());

  // Rediriger si pas connecté
  if (!isAuthenticated) {
    return <Navigate to="/admin/login" replace />;
  }

  // Statistiques rapides
  const stats = {
    totalBookings: bookings?.length || 0,
    totalContacts: contacts?.length || 0,
    pendingBookings: bookings?.length || 0, // À implémenter selon statut
    todayBookings: 0 // À calculer selon la date
  };

  const menuItems = [
    {
      title: 'Agenda',
      description: 'Gérer les rendez-vous et plannings',
      icon: Calendar,
      href: '/admin/agenda',
      color: 'bg-blue-500',
      count: stats.totalBookings
    },
    {
      title: 'Messages',
      description: 'Consulter les demandes de contact',
      icon: FileText,
      href: '/admin/messages',
      color: 'bg-green-500',
      count: stats.totalContacts
    },
    {
      title: 'Réglages',
      description: 'Modifier textes et photos du site',
      icon: Settings,
      href: '/admin/settings',
      color: 'bg-purple-500'
    },
    {
      title: 'Contenu',
      description: 'Modifier tous les textes du site',
      icon: FileText,
      href: '/admin/content',
      color: 'bg-indigo-500'
    }
  ];

  const recentBookings = bookings?.slice(0, 3) || [];

  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white shadow-sm border-b">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-4">
            <div className="flex items-center space-x-3">
              <div className="flex items-center justify-center w-10 h-10 bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg">
                <Car className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Administration AutoSpa 77</h1>
                <p className="text-sm text-gray-600">Tableau de bord</p>
              </div>
            </div>
            <Button
              onClick={logout}
              variant="outline"
              className="flex items-center space-x-2"
            >
              <LogOut className="w-4 h-4" />
              <span>Déconnexion</span>
            </Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {/* Statistiques */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Calendar className="h-8 w-8 text-blue-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Réservations</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalBookings}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <FileText className="h-8 w-8 text-green-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Messages</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.totalContacts}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <Clock className="h-8 w-8 text-orange-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">En attente</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.pendingBookings}</p>
                </div>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardContent className="p-6">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <CheckCircle className="h-8 w-8 text-purple-500" />
                </div>
                <div className="ml-4">
                  <p className="text-sm font-medium text-gray-600">Aujourd'hui</p>
                  <p className="text-2xl font-bold text-gray-900">{stats.todayBookings}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Menu principal */}
          <div className="lg:col-span-2">
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Actions rapides</h2>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
              {menuItems.map((item) => (
                <Link key={item.title} to={item.href}>
                  <Card className="hover:shadow-lg transition-shadow duration-200 cursor-pointer">
                    <CardContent className="p-6">
                      <div className="flex items-center space-x-4">
                        <div className={`p-3 rounded-lg ${item.color}`}>
                          <item.icon className="w-6 h-6 text-white" />
                        </div>
                        <div className="flex-1">
                          <div className="flex items-center justify-between">
                            <h3 className="text-lg font-medium text-gray-900">{item.title}</h3>
                            {item.count !== undefined && (
                              <Badge variant="secondary" className="ml-2">
                                {item.count}
                              </Badge>
                            )}
                          </div>
                          <p className="text-sm text-gray-600 mt-1">{item.description}</p>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </Link>
              ))}
            </div>
          </div>

          {/* Réservations récentes */}
          <div>
            <h2 className="text-lg font-semibold text-gray-900 mb-4">Réservations récentes</h2>
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center justify-between text-base">
                  <span>Dernières demandes</span>
                  <Link to="/admin/agenda">
                    <Button variant="outline" size="sm">Voir tout</Button>
                  </Link>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {recentBookings.length > 0 ? (
                  <div className="space-y-4">
                    {recentBookings.map((booking) => (
                      <div key={booking.id} className="border-b border-gray-100 pb-3 last:border-0">
                        <div className="flex justify-between items-start">
                          <div>
                            <p className="font-medium text-sm">
                              {booking.client?.firstName} {booking.client?.lastName}
                            </p>
                            <p className="text-xs text-gray-500">
                              {booking.date} à {booking.time}
                            </p>
                          </div>
                          <Badge className="bg-orange-100 text-orange-800 text-xs">
                            Nouveau
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-6 text-gray-500">
                    <Calendar className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                    <p className="text-sm">Aucune réservation récente</p>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard;